import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import OTPInput from "react-otp-input";
import { images } from "../assets/images";
import Calendar from "../components/Calender";
import storage from "../assets/js/Global";
import {
  CalendarDays,
  X,
  User,
  Phone,
  Mail,
  Venus,
  Mars,
  BarChart2,
  Target,
  TrendingUp,
  DollarSign,
  Lock,
  Eye,
  EyeOff,
  ClipboardList,
  Coins,
  Trophy,
  ChevronRight,
  Bell,
  Trash2,
} from "lucide-react";
import { useGlobal } from "../context/GlobalContext";
import Cropper from "react-cropper";
import SalesDatePicker from "../components/SalesDatePicker";

const PRESET_LABELS = {
  today: "Today's",
  yesterday: "Yesterday",
  last_7_days: "Last 7 Days",
  last_30_days: "Last 30 Days",
  this_month: "This Month",
  last_week: "Last Week",
  last_month: "Last Month",
  last_year: "Last Year",
};

const MENU = [
  {
    id: "personal",
    label: "Personal Info",
    Icon: User,
    color: "#dbeafe",
    iconColor: "#2563eb",
  },
  // {
  //   id: "sales",
  //   label: "Sales Performance",
  //   Icon: BarChart2,
  //   color: "#dcfce7",
  //   iconColor: "#16a34a",
  // },
  {
    id: "password",
    label: "Change Password",
    Icon: Lock,
    color: "#fce7f3",
    iconColor: "#db2777",
  },
  {
    id: "stats",
    label: "Work Stats",
    Icon: Trophy,
    color: "#fef9c3",
    iconColor: "#d97706",
  },
];

const PersonalInfoSection = ({
  profileData,
  handleChange,
  updateProfile,
  setProfileData,
}) => {
  const calendarRef = useRef(null);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    if (!showCalendar) return;
    const handleOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [showCalendar]);

  return (
    <>
      <div className="pf2-section-header">
        <div className="pf2-section-icon" style={{ background: "#dbeafe" }}>
          <User size={18} color="#2563eb" />
        </div>
        <div>
          <h5 className="pf2-section-title">Personal Information</h5>
          <p className="pf2-section-subtitle">Update your personal details</p>
        </div>
      </div>

      <div className="pf2-section-body">
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="row g-3">
            <div className="col-12 col-sm-6">
              <div className="pf-field">
                <label className="pf-label">
                  First Name
                  {!profileData.firstName && (
                    <span className="text-danger"> *</span>
                  )}
                </label>
                <input
                  type="text"
                  name="firstName"
                  className="pf-input"
                  placeholder="Enter first name"
                  value={profileData.firstName ?? ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div className="pf-field">
                <label className="pf-label">
                  Last Name
                  {!profileData.lastName && (
                    <span className="text-danger"> *</span>
                  )}
                </label>
                <input
                  type="text"
                  name="lastName"
                  className="pf-input"
                  placeholder="Enter last name"
                  value={profileData.lastName ?? ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div className="pf-field" ref={calendarRef}>
                <label className="pf-label">
                  Date of Birth
                  {!profileData.dob && <span className="text-danger"> *</span>}
                </label>
                <div className="pf-input-icon-wrap">
                  <div
                    className={`pf-input pf-input--select${showCalendar ? " pf-input--active" : ""}`}
                    onClick={() => setShowCalendar(!showCalendar)}
                  >
                    {profileData.dob
                      ? profileData.dob.split("-").reverse().join("/")
                      : "DD / MM / YYYY"}
                  </div>
                  <CalendarDays
                    size={15}
                    className="pf-input-icon"
                    pointerEvents="none"
                  />
                </div>
                {showCalendar && (
                  <div className="pf-calendar-drop">
                    <Calendar
                      mode="dob"
                      value={profileData.dob}
                      onDateChange={(selectedDate) => {
                        const [day, month, year] = selectedDate.split("/");
                        setProfileData((prev) => ({
                          ...prev,
                          dob: `${year}-${month}-${day}`,
                        }));
                        setShowCalendar(false);
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div className="pf-field">
                <label className="pf-label">Gender</label>
                <div className="pf-gender-row">
                  <label
                    className={`pf-gender-pill${profileData.gender === "male" ? " active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={profileData.gender === "male"}
                      onChange={handleChange}
                    />
                    <Mars size={14} /> Male
                  </label>
                  <label
                    className={`pf-gender-pill${profileData.gender === "female" ? " active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={profileData.gender === "female"}
                      onChange={handleChange}
                    />
                    <Venus size={14} /> Female
                  </label>
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div className="pf-field">
                <label className="pf-label">
                  Email <span className="pf-label-note">read-only</span>
                </label>
                <div className="pf-input-icon-wrap">
                  <input
                    type="text"
                    className="pf-input pf-input--disabled"
                    value={profileData.email ?? ""}
                    disabled
                  />
                  <Mail size={14} className="pf-input-icon" />
                </div>
              </div>
            </div>

            <div className="col-12 col-sm-6">
              <div className="pf-field">
                <label className="pf-label">
                  Phone Number
                  {!profileData.phone && (
                    <span className="text-danger"> *</span>
                  )}
                </label>
                <div className="pf-input-icon-wrap">
                  <input
                    type="tel"
                    name="phone"
                    inputMode="tel"
                    className="pf-input"
                    placeholder="Enter phone number"
                    value={profileData.phone ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const sanitized =
                        (raw.startsWith("+") ? "+" : "") +
                        raw.replace(/[^0-9]/g, "");
                      handleChange({
                        target: { name: "phone", value: sanitized },
                      });
                    }}
                  />
                  <Phone size={14} className="pf-input-icon" />
                </div>
              </div>
            </div>

            <div className="col-12 text-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={updateProfile}
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

const SalesSection = ({ getSalesStats }) => {
  const [filter, setFilter] = useState({
    type: "preset",
    preset: "this_month",
  });
  const [data, setData] = useState({});

  useEffect(() => {
    getSalesStats(filter).then((d) => setData(d || {}));
  }, [filter, getSalesStats]);

  const leadsObj = typeof data?.leads === "object" ? data.leads : null;
  const leads = leadsObj ? leadsObj.total || 0 : data?.leads || 0;
  const converted = leadsObj ? leadsObj.converted || 0 : data?.converted || 0;
  const leadsPercent = leadsObj
    ? leadsObj.percentage || 0
    : leads > 0
      ? Math.round((converted / leads) * 100)
      : 0;

  const targetObj = typeof data?.target === "object" ? data.target : null;
  const target = targetObj ? targetObj.value || 0 : data?.target || 0;
  const achieved = targetObj ? targetObj.achieved || 0 : data?.achieved || 0;
  const targetPercent = targetObj
    ? targetObj.percentage || 0
    : target > 0
      ? Math.round((achieved / target) * 100)
      : 0;

  const periodLabel =
    filter.type === "preset"
      ? PRESET_LABELS[filter.preset] || filter.preset
      : filter.type === "range"
        ? "Custom Range"
        : filter.date || "";

  return (
    <>
      <div className="pf2-section-header mb-3">
        <div className="pf2-section-icon" style={{ background: "#dcfce7" }}>
          <BarChart2 size={18} color="#16a34a" />
        </div>
        <div>
          <h5 className="pf2-section-title">Sales Performance</h5>
          <p className="pf2-section-subtitle">Your sales metrics</p>
        </div>
      </div>

      <SalesDatePicker defaultPreset="this_month" onChange={setFilter} />

      <div className="pf2-section-body p-0">
        <div className="sp2-grid">
          <div className="sp2-card sp2-card--blue">
            <div className="sp2-card-head">
              <span className="sp2-badge sp2-badge--blue">
                <Target size={13} />
              </span>
              <span className="sp2-label">Leads / Converted</span>
            </div>
            <div className="sp2-val">
              {leads} / {converted}
            </div>
            <div className="sp2-bar">
              <div
                className="sp2-bar-fill sp2-bar-fill--blue"
                style={{ width: `${Math.min(leadsPercent, 100)}%` }}
              />
            </div>
            <div className="sp2-foot">
              <span className="sp2-pct sp2-pct--blue">{leadsPercent}%</span>
            </div>
          </div>

          <div className="sp2-card sp2-card--green">
            <div className="sp2-card-head">
              <span className="sp2-badge sp2-badge--green">
                <DollarSign size={13} />
              </span>
              <span className="sp2-label">{periodLabel} Revenue</span>
            </div>
            <div className="sp2-val">${data?.revenue || 0}</div>
          </div>

          <div className="sp2-card sp2-card--purple">
            <div className="sp2-card-head">
              <span className="sp2-badge sp2-badge--purple">
                <TrendingUp size={13} />
              </span>
              <span className="sp2-label">Target Achieved</span>
            </div>
            <div className="sp2-val">
              ${achieved} / ${target}
            </div>
            <div className="sp2-bar">
              <div
                className="sp2-bar-fill sp2-bar-fill--purple"
                style={{ width: `${Math.min(targetPercent, 100)}%` }}
              />
            </div>
            <div className="sp2-foot">
              <span className="sp2-pct sp2-pct--purple">{targetPercent}%</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

/* Reusable step indicator — previous (completed) steps are clickable to go back. */
const StepIndicator = ({ step, steps, activeColor = "#dc2626", onStepClick }) => (
  <div className="pf2-steps mb-4">
    {steps.map((n, i) => {
      const clickable = typeof onStepClick === "function" && n < step;
      return (
        <React.Fragment key={n}>
          <div
            className={`pf2-step-dot ${step >= n ? "active" : ""} ${step > n ? "done" : ""} ${clickable ? "clickable" : ""}`}
            style={
              step === n
                ? { background: activeColor, borderColor: activeColor }
                : undefined
            }
            onClick={clickable ? () => onStepClick(n) : undefined}
          >
            {n}
          </div>
          {i < steps.length - 1 && (
            <div className={`pf2-step-line ${step > n ? "active" : ""}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const ChangePasswordSection = () => {
  const { userEmailVerify, setPassword } = useGlobal();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [show, setShow] = useState({ password: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const userEmail = storage.get("user")?.email || "";

  const handleSendOtp = async () => {
    setBusy(true);
    const ok = await userEmailVerify();
    setBusy(false);
    if (ok) setStep(2);
  };

  const handleSubmit = async () => {
    setBusy(true);
    const ok = await setPassword({
      otp,
      password: form.password,
      confirmPassword: form.confirm,
    });
    setBusy(false);
    if (ok) {
      setStep(1);
      setOtp("");
      setForm({ password: "", confirm: "" });
    }
  };

  return (
    <>
      <div className="pf2-section-header">
        <div className="pf2-section-icon" style={{ background: "#fce7f3" }}>
          <Lock size={18} color="#db2777" />
        </div>
        <div>
          <h5 className="pf2-section-title">Change Password</h5>
          <p className="pf2-section-subtitle">
            {step === 1
              ? "Send a verification code to your email"
              : "Enter the code and your new password"}
          </p>
        </div>
      </div>

      <div className="pf2-section-body">
        <StepIndicator
          step={step}
          steps={[1, 2]}
          activeColor="#db2777"
          onStepClick={setStep}
        />
        <div className="pf2-cp-step">
          {step === 1 ? (
            <>
              <p className="pf2-cp-hint">
                We'll send a 6-digit verification code to your registered email
                address.
              </p>
              <div className="pf2-cp-email-chip">{userEmail}</div>
              <div className="text-center">
                <button
                  className="btn btn-secondary"
                  onClick={handleSendOtp}
                  disabled={busy}
                >
                  {busy ? "Sending…" : "Send Verification Code"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="d-flex align-items-center justify-content-between mb-1">
                <p className="mb-0 fs-13 text-muted">
                  Code sent to <strong>{userEmail}</strong>
                </p>
              </div>
              <p className="fs-13 text-muted mb-3">
                Enter the 6-digit code from your email.
              </p>

              <div className="d-flex justify-content-center mb-4">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  inputType="tel"
                  renderSeparator={<span className="mx-1" />}
                  renderInput={(props) => (
                    <input {...props} className="otp-input" />
                  )}
                />
              </div>

              <div className="pf-field mb-3">
                <label className="pf-label">
                  New Password
                  {!form.password && <span className="text-danger"> *</span>}
                </label>
                <div className="pf-input-icon-wrap">
                  <input
                    type={show.password ? "text" : "password"}
                    className="pf-input"
                    placeholder="Enter new password"
                    value={form.password}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, password: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="pf-input-icon bg-transparent border-0 p-0"
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                    onClick={() =>
                      setShow((p) => ({ ...p, password: !p.password }))
                    }
                  >
                    {show.password ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div className="pf-field mb-4">
                <label className="pf-label">
                  Confirm Password
                  {!form.confirm && <span className="text-danger"> *</span>}
                </label>
                <div className="pf-input-icon-wrap">
                  <input
                    type={show.confirm ? "text" : "password"}
                    className="pf-input"
                    placeholder="Re-enter new password"
                    value={form.confirm}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, confirm: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="pf-input-icon bg-transparent border-0 p-0"
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                    onClick={() =>
                      setShow((p) => ({ ...p, confirm: !p.confirm }))
                    }
                  >
                    {show.confirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>

              <div className="text-center">
                <button
                  className="btn btn-secondary"
                  onClick={handleSubmit}
                  disabled={busy || otp.length < 6}
                >
                  {busy ? "Changing…" : "Change Password"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

/* ─── Work Stats ──────────────────────────────────────────── */
const WorkStatsSection = ({ getPointSummary, getJobsList }) => {
  const [stats, setStats] = useState({});
  const [assigned, setAssigned] = useState(0);
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    (async () => {
      const [a, c] = await Promise.all([
        getJobsList({ status: "assigned", page: 1 }),
        getJobsList({ status: "completed", page: 1 }),
      ]);
      setAssigned(a?.pagination?.total || 0);
      setCompleted(c?.pagination?.total || 0);
    })();
    getPointSummary().then((d) => setStats(d || {}));
  }, []);

  const total = assigned + completed;
  const completionRate = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <>
      <div className="pf2-section-header">
        <div className="pf2-section-icon" style={{ background: "#fef9c3" }}>
          <Trophy size={18} color="#d97706" />
        </div>
        <div>
          <h5 className="pf2-section-title">Work Stats</h5>
          <p className="pf2-section-subtitle">Your performance overview</p>
        </div>
      </div>

      <div className="pf2-section-body">
        {/* Jobs card */}
        <div className="ws-jobs-card mb-4">
          <div className="ws-jobs-title">
            <div className="ws-jobs-title-icon">
              <ClipboardList size={15} />
            </div>
            <span className="ws-jobs-title-text">Jobs Overview</span>
          </div>

          <div className="ws-stat-row">
            <div className="ws-stat">
              <div className="ws-stat-val">{assigned}</div>
              <div className="ws-stat-lbl">Assigned</div>
            </div>
            <div className="ws-stat">
              <div className="ws-stat-val">{completed}</div>
              <div className="ws-stat-lbl">Completed</div>
            </div>
            <div className="ws-stat">
              <div className="ws-stat-val">{total}</div>
              <div className="ws-stat-lbl">Total</div>
            </div>
          </div>

          {total > 0 && (
            <div className="ws-completion">
              <span className="ws-completion-label">Completion</span>
              <div className="ws-completion-bar">
                <div
                  className="ws-completion-fill"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
              <span className="ws-completion-pct">{completionRate}%</span>
            </div>
          )}
        </div>

        {/* Points */}
        <p className="pf2-stat-group-label">
          <Coins size={13} className="me-1" /> Points Summary
        </p>
        <div className="ws-points-grid">
          <div className="ws-points-card ws-points-card--blue">
            <div className="ws-points-val">{stats.total_earned || 0}</div>
            <div className="ws-points-lbl">Total Earned</div>
          </div>
          <div className="ws-points-card ws-points-card--green">
            <div className="ws-points-val">{stats.balance || 0}</div>
            <div className="ws-points-lbl">Balance</div>
          </div>
          <div className="ws-points-card ws-points-card--red">
            <div className="ws-points-val">{stats.total_redeemed || 0}</div>
            <div className="ws-points-lbl">Redeemed</div>
          </div>
        </div>
      </div>
    </>
  );
};

const DeleteAccountSection = () => {
  const { userEmailVerify, deleteAccountVerifyOtp, deleteAccount } =
    useGlobal();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const userEmail = storage.get("user")?.email || "";

  const handleSendOtp = async () => {
    setBusy(true);
    const ok = await userEmailVerify({
      module: "delete-account",
      signKey: "delete_acc_sign",
    });
    setBusy(false);
    if (ok) setStep(2);
  };

  const handleVerifyOtp = async () => {
    setBusy(true);
    const ok = await deleteAccountVerifyOtp(otp);
    setBusy(false);
    if (ok) setStep(3);
  };

  const handleDelete = async () => {
    if (!confirmChecked) {
      toast.error("Please confirm before deleting your account");
      return;
    }
    setBusy(true);
    const ok = await deleteAccount();
    setBusy(false);
    if (ok) navigate("/login");
  };

  return (
    <>
      <div className="pf2-section-header">
        <div className="pf2-section-icon" style={{ background: "#fee2e2" }}>
          <Trash2 size={18} color="#dc2626" />
        </div>
        <div>
          <h5 className="pf2-section-title">Delete Account</h5>
          <p className="pf2-section-subtitle">
            {step === 1
              ? "Send a verification code to your email"
              : step === 2
                ? "Enter the code sent to your email"
                : "Confirm permanent account deletion"}
          </p>
        </div>
      </div>

      <div className="pf2-section-body">
        {/* Step indicator */}
        <StepIndicator
          step={step}
          steps={[1, 2, 3]}
          activeColor="#dc2626"
          onStepClick={setStep}
        />

        {step === 1 && (
          <div className="pf2-cp-step">
            <div className="pf2-danger-note mb-3">
              Deleting your account is permanent. All your data will be removed
              and cannot be recovered.
            </div>
            <p className="pf2-cp-hint">
              To continue, we'll send a 6-digit verification code to your
              registered email address.
            </p>
            <div className="pf2-cp-email-chip">{userEmail}</div>
            <div className="text-center">
              <button
                className="btn btn-danger"
                onClick={handleSendOtp}
                disabled={busy}
              >
                {busy ? "Sending…" : "Send Verification Code"}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="pf2-cp-step">
            <p className="mb-1 fs-13 text-muted text-center">
              Code sent to <strong>{userEmail}</strong>
            </p>
            <p className="fs-13 text-muted mb-3 text-center">
              Enter the 6-digit code from your email.
            </p>

            <div className="d-flex justify-content-center mb-4">
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                inputType="tel"
                renderSeparator={<span className="mx-1" />}
                renderInput={(props) => (
                  <input {...props} className="otp-input" />
                )}
              />
            </div>

            <div className="d-flex flex-column align-items-center gap-2">
              <button
                className="btn btn-danger"
                onClick={handleVerifyOtp}
                disabled={busy || otp.length < 6}
              >
                {busy ? "Verifying…" : "Verify Code"}
              </button>
              <button
                type="button"
                className="btn btn-link text-muted fs-13 p-0"
                onClick={handleSendOtp}
                disabled={busy}
              >
                Resend Code
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="pf2-cp-step">
            <div className="pf2-danger-note mb-3">
              This is your final step. Once deleted, your account and all
              associated data will be permanently removed. This action cannot be
              undone.
            </div>

            <label className="pf2-confirm-check">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
              />
              <span>
                I understand that this action is permanent and I want to delete
                my account.
              </span>
            </label>

            <div className="text-center mt-4">
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={busy || !confirmChecked}
              >
                {busy ? "Deleting…" : "Delete My Account"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const ProfileScreen = () => {
  const {
    uploadProfilePic,
    profileData,
    setProfileData,
    preview,
    setPreview,
    getUserProfile,
    updateProfile,
    getSalesStats,
    getPointSummary,
    getJobsList,
    toggleNotification,
  } = useGlobal();

  const fileInputRef = useRef(null);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [cropper, setCropper] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [activeSection, setActiveSection] = useState("personal");
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const user = storage.get("user");
    if (user?.is_push_notification_enable !== undefined) {
      return !!user.is_push_notification_enable;
    }
    const saved = storage.get("notificationsEnabled");
    return saved === null || saved === undefined ? true : !!saved;
  });
  const [notifBusy, setNotifBusy] = useState(false);

  const toggleNotifications = async () => {
    if (notifBusy) return;
    const next = !notificationsEnabled;
    setNotificationsEnabled(next);
    setNotifBusy(true);
    const ok = await toggleNotification(next);
    setNotifBusy(false);
    if (!ok) setNotificationsEnabled(!next);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    getUserProfile();
  }, [getUserProfile]);

  const handleCrop = () => {
    if (cropper) {
      const canvas = cropper.getCroppedCanvas();
      if (canvas) {
        const base64Image = canvas.toDataURL("image/jpeg");
        setPreview(base64Image);
        setShowCropper(false);
        uploadProfilePic(base64Image);
      }
    }
  };
  const displayName =
    profileData.firstName || profileData.lastName
      ? `${(profileData.firstName?.charAt(0).toUpperCase() || "") + (profileData.firstName?.slice(1) || "")} ${(profileData.lastName?.charAt(0).toUpperCase() || "") + (profileData.lastName?.slice(1) || "")}`.trim()
      : "Your Name";

  return (
    <div className="content-part pf2-page">
      <div className="d-lg-none mb-3">
        <div className="pf2-mobile-profile">
          <div
            className="pf-avatar-wrap mx-auto"
            onClick={() => fileInputRef.current.click()}
            title="Change photo"
          >
            <div className="pf-avatar">
              <img src={preview || images.userAvatar} alt="Profile" />
            </div>
            <span className="pf-camera-btn">
              <i className="fa-solid fa-camera" />
            </span>
          </div>
          <h5 className="pf2-mobile-name">{displayName}</h5>
          <p className="pf2-mobile-email">{profileData.email || "—"}</p>
        </div>

        <div className="pf2-segtabs">
          {MENU.map(({ id, label, Icon }) => (
            <button
              key={id}
              className={`pf2-segtab ${activeSection === id ? "active" : ""}`}
              onClick={() => setActiveSection(id)}
            >
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
          <button
            className={`pf2-segtab pf2-segtab--danger ${activeSection === "delete" ? "active" : ""}`}
            onClick={() => setActiveSection("delete")}
          >
            <Trash2 size={14} />
            <span>Delete Account</span>
          </button>
        </div>

        <div className="pf2-mobile-toggle-row mt-2">
          <div className="d-flex align-items-center gap-2">
            <span
              className="pf2-nav-icon"
              style={{ background: "#ede9fe", color: "#7c3aed" }}
            >
              <Bell size={14} />
            </span>
            <span className="fw-semibold fs-14">Notifications</span>
          </div>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="pf2-notifications-toggle-mobile"
              checked={notificationsEnabled}
              onChange={toggleNotifications}
              disabled={notifBusy}
            />
          </div>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="row g-3 align-items-start">
        {/* Left nav — desktop only */}
        <div className="d-none d-lg-block col-lg-4 col-xl-3">
          <div className="pf2-nav-panel">
            <div className="pf2-nav-hero">
              <div
                className="pf-avatar-wrap mx-auto"
                onClick={() => fileInputRef.current.click()}
                title="Change photo"
              >
                <div className="pf-avatar">
                  <img src={preview || images.userAvatar} alt="Profile" />
                </div>
                <span className="pf-camera-btn">
                  <i className="fa-solid fa-camera" />
                </span>
              </div>
              <h5 className="pf2-nav-name">{displayName}</h5>
              <p className="pf2-nav-email">{profileData.email || "—"}</p>
            </div>

            <nav className="pf2-nav-menu">
              {MENU.map(({ id, label, Icon, color, iconColor }) => (
                <button
                  key={id}
                  className={`pf2-nav-item ${activeSection === id ? "active" : ""}`}
                  onClick={() => setActiveSection(id)}
                >
                  <span
                    className="pf2-nav-icon"
                    style={
                      activeSection === id
                        ? { background: color, color: iconColor }
                        : {}
                    }
                  >
                    <Icon size={15} />
                  </span>
                  {label}
                  <ChevronRight size={14} className="ms-auto opacity-40" />
                </button>
              ))}

              <div className="pf2-nav-item pf2-nav-item--toggle">
                <span
                  className="pf2-nav-icon"
                  style={{ background: "#ede9fe", color: "#7c3aed" }}
                >
                  <Bell size={15} />
                </span>
                Notifications
                <div className="form-check form-switch ms-auto mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="pf2-notifications-toggle"
                    checked={notificationsEnabled}
                    onChange={toggleNotifications}
                    disabled={notifBusy}
                  />
                </div>
              </div>
              <button
                className={`pf2-nav-item pf2-nav-item--danger ${activeSection === "delete" ? "active" : ""}`}
                onClick={() => setActiveSection("delete")}
              >
                <span
                  className="pf2-nav-icon"
                  style={{ background: "#fee2e2", color: "#dc2626" }}
                >
                  <Trash2 size={15} />
                </span>
                Delete Account
                <ChevronRight size={14} className="ms-auto opacity-40" />
              </button>
            </nav>
          </div>
        </div>

        <div className="col-12 col-lg-8 col-xl-9">
          <div className="pf2-content-area">
            {activeSection === "personal" && (
              <PersonalInfoSection
                profileData={profileData}
                handleChange={handleChange}
                updateProfile={updateProfile}
                setProfileData={setProfileData}
              />
            )}
            {activeSection === "sales" && (
              <SalesSection getSalesStats={getSalesStats} />
            )}
            {activeSection === "password" && <ChangePasswordSection />}
            {activeSection === "stats" && (
              <WorkStatsSection
                getPointSummary={getPointSummary}
                getJobsList={getJobsList}
              />
            )}
            {activeSection === "delete" && <DeleteAccountSection />}
          </div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            setImageToCrop(URL.createObjectURL(file));
            setShowCropper(true);
          }
          e.target.value = null;
        }}
      />

      {showCropper && (
        <div className="edit-crop-overlay modal-backdrop-custom">
          <div className="edit-crop-modal crop-modal">
            <div className="edit-crop-header">
              <h5 className="edit-crop-title">Crop Image</h5>
              <button
                className="edit-crop-close"
                onClick={() => setShowCropper(false)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <Cropper
              src={imageToCrop}
              style={{ height: 300, width: "100%" }}
              initialAspectRatio={1}
              aspectRatio={1}
              guides={false}
              viewMode={2}
              responsive={true}
              autoCropArea={1}
              minCropBoxHeight={200}
              onInitialized={(instance) => setCropper(instance)}
            />
            <div className="edit-crop-actions">
              <button
                className="btn btn-secondary"
                onClick={() => cropper?.reset()}
              >
                Restore
              </button>
              <button className="btn btn-primary" onClick={handleCrop}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
